/**
 * Zig-zag (Pinterest) masonry helpers for Shop + Content catalog grids.
 * Shortest-column packing + responsive cols.
 */

export const MASONRY_COL_BREAKPOINTS: ReadonlyArray<{ min: number; cols: number }> = [
  { min: 1240, cols: 6 },
  { min: 1020, cols: 5 },
  { min: 820, cols: 4 },
  { min: 600, cols: 3 },
  { min: 0, cols: 2 },
];

/** Product card aspect palette (width/height) for zigzag variety. */
const PRODUCT_RATIO_PALETTE = [
  0.66, 0.75, 0.8, 0.9, 1, 1.1, 1.2, 1.33,
] as const;

/**
 * Clamp wild aspect ratios so one image can't distort a masonry column.
 * Ratio is width/height (CSS aspect-ratio).
 */
export function safeMediaRatio(r: number | null | undefined): number {
  if (r == null || Number.isNaN(r) || r <= 0) return 0.75;
  return Math.min(2, Math.max(0.5, r));
}

export function colsForContainerWidth(width: number): number {
  const w = Math.max(0, width);
  for (const bp of MASONRY_COL_BREAKPOINTS) {
    if (w >= bp.min) return bp.cols;
  }
  return 2;
}

/** Stable pseudo-random product ratio from id (zigzag before image measures). */
export function productMediaRatioFromId(id: string): number {
  let h = 0;
  const s = String(id || "");
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return PRODUCT_RATIO_PALETTE[h % PRODUCT_RATIO_PALETTE.length]!;
}

type ContentRatioInput = {
  isText?: boolean;
  isVideo?: boolean;
  platformSlug?: string;
  formatSlug?: string | null;
  type?: string;
};

/**
 * Native content media aspect by platform / format (artifact AR map).
 * Returns null for text cards (no media frame).
 */
export function contentMediaRatio(item: ContentRatioInput): number | null {
  if (item.isText) return null;
  const p = String(item.platformSlug || "").toLowerCase();
  const format = String(item.formatSlug || "").toLowerCase();
  const t = String(item.type || "").trim().toUpperCase();

  if (p === "youtube") {
    if (format === "shorts" || t === "SHORTS" || t === "SHORT") return 9 / 16;
    return 16 / 9;
  }
  if (p === "tiktok") return 9 / 16;
  if (p === "instagram") {
    if (format === "reels" || t === "REELS") return 9 / 16;
    if (format === "stories" || t === "STORY" || t === "HIGHLIGHTS") return 9 / 16;
    // Posts: mix portrait + square for zigzag
    return t.includes("CAROUSEL") || format === "posts" ? 4 / 5 : 1;
  }
  if (item.isVideo) return 9 / 16;
  return 4 / 5;
}

export type MasonryPackItem = {
  /** Relative media height unit ≈ 1/ratio (+ caption budget). */
  weight: number;
};

/**
 * `fillRows`: place in list order, left to right, and only then the next row.
 * Otherwise every card goes in the current shortest column so the bottoms stay level.
 */
export function packMasonryColumns<T extends MasonryPackItem>(
  items: T[],
  cols: number,
  fillRows = false,
): T[][] {
  const n = Math.max(1, cols);
  if (!items.length) return [];

  const columns: T[][] = Array.from({ length: n }, () => []);

  if (fillRows) {
    items.forEach((item, i) => {
      columns[i % n]!.push(item);
    });
  } else {
    // Every card, including the tail, goes in the current shortest column
    // so the bottoms stay level. Parking the tail on the left stacked extra
    // height onto column 0 and left a hole beside the other columns.
    const heights = Array.from({ length: n }, () => 0);
    for (const item of items) {
      let shortest = 0;
      for (let c = 1; c < n; c += 1) {
        if (heights[c]! < heights[shortest]!) shortest = c;
      }
      columns[shortest]!.push(item);
      heights[shortest]! += Math.max(0.2, item.weight);
    }
  }

  while (columns.length > 1 && columns[columns.length - 1]!.length === 0) {
    columns.pop();
  }
  return columns;
}

/**
 * Product frames are capped at 300px, so a portrait ratio must not be packed
 * as twice the height of a landscape one — that stretch is what opened a
 * multi-screen gap under the shorter columns.
 */
export function productPackWeight(ratio: number | null | undefined): number {
  const r = safeMediaRatio(ratio);
  const col = 220;
  const frame = Math.min(300, col / r);
  return (frame + 64) / col;
}

/**
 * Content tiles use the real aspect box (no 300px cap). Same unit as
 * `productPackWeight` so a Search grid can mix both without a tall column.
 */
export function contentPackWeight(
  ratio: number | null | undefined,
  opts?: { isText?: boolean; hasCaption?: boolean },
): number {
  if (opts?.isText) return opts.hasCaption ? 1.35 : 0.9;
  const r = safeMediaRatio(ratio ?? 0.8);
  const col = 220;
  const frame = col / r;
  const caption = opts?.hasCaption ? 52 : 12;
  return (frame + caption) / col;
}

export function masonryWeightForRatio(
  ratio: number | null,
  opts?: { isText?: boolean; hasCaption?: boolean; hasLinkPreview?: boolean },
): number {
  if (opts?.isText) {
    return opts.hasLinkPreview ? 2.4 : 1.5;
  }
  const r = safeMediaRatio(ratio);
  const captionBudget = opts?.hasCaption ? 0.5 : 0.28;
  return 1 / r + captionBudget;
}
